import { FC, forwardRef } from "react";

import { Theme, useTheme, SxProps } from "@mui/material/styles";

import { DashboardButton } from "@/dashboard-design-system/Button/DashboardButton";
import { DashboardButtonProps } from "@/dashboard-design-system/Button/types";
import { DashboardIcon, DashboardIconProps } from "@/dashboard-design-system/Icon";
import { mergeSx } from "@/helpers/mergeSx";
import { IconElement } from "@/icons/types";

export const buttonSizeOptions = ["extraSmall", "small", "medium", "large"] as const;
export type ButtonSize = (typeof buttonSizeOptions)[number];

export type DashboardIconButtonProps = Omit<
  DashboardButtonProps,
  "fullWidth" | "startIcon" | "endIcon" | "children" | "size" | "slotProps"
> & {
  children: IconElement;
  size?: ButtonSize;
  slotProps?: {
    icon?: {
      sx?: SxProps<Theme>;
    };
  };
};

const ICON_BUTTON_SIZE_MAP: Record<ButtonSize, (theme: Theme) => number> = Object.freeze({
  extraSmall: () => 24,
  small: (theme: Theme) => theme.button.size.default.small,
  medium: (theme: Theme) => theme.button.size.default.medium,
  large: (theme: Theme) => theme.button.size.default.large,
});

const ICON_SIZE_MAP: Record<ButtonSize, DashboardIconProps["size"]> = Object.freeze({
  extraSmall: "small",
  small: "medium",
  medium: "medium",
  large: "medium",
});

const ICON_BUTTON_TO_BUTTON_SIZE_MAP: Record<ButtonSize, DashboardButtonProps["size"]> = Object.freeze({
  extraSmall: "small",
  small: "small",
  medium: "medium",
  large: "large",
});

export const DashboardIconButton: FC<DashboardIconButtonProps> = forwardRef<
  HTMLButtonElement,
  DashboardIconButtonProps
>(({ children, size = "medium", sx, slotProps, ...rest }, ref) => {
  const theme = useTheme();

  const styles = mergeSx(
    {
      // Reset button styles
      padding: 0,
      minHeight: 0,
      lineHeight: "100%",
      width: ICON_BUTTON_SIZE_MAP[size](theme),
      height: ICON_BUTTON_SIZE_MAP[size](theme),
      ...(size === "extraSmall" && {
        borderRadius: 0.75,
      }),
    },
    sx
  );

  return (
    <DashboardButton ref={ref} sx={styles} size={ICON_BUTTON_TO_BUTTON_SIZE_MAP[size]} {...rest}>
      <DashboardIcon vibe="currentColor" size={ICON_SIZE_MAP[size]} sx={slotProps?.icon?.sx}>
        {children}
      </DashboardIcon>
    </DashboardButton>
  );
});

DashboardIconButton.displayName = "DashboardIconButton";
